<template>
  <v-form>
    <div class="mb-4">
      <v-row dense>
        <v-col cols="12" sm="6">
          <px-autocomplete-comprador
            dense
            required
            label="Local de faturamento"
            class="required rounded-tag-small"
            :error="$v.cotacao.comprador.$error"
            @blur="$v.cotacao.comprador.$touch()"
            v-model="cotacao.comprador"/>
        </v-col>
        <v-col cols="12" sm="6">
          <px-autocomplete-locais-entrega
            dense
            required
            class="required rounded-tag-small"
            :error="$v.cotacao.locaisEntrega.$error"
            @blur="$v.cotacao.locaisEntrega.$touch()"
            v-model="cotacao.locaisEntrega"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col class="d-flex" cols="12" sm="4">
          <px-select-enum dense
                          label="Tipo de frete"
                          class="rounded-tag-small"
                          :items="tiposFretes"
                          v-model="cotacao.tipoFrete"/>
        </v-col>
        <v-col class="d-flex" cols="12" sm="4">
          <px-date-picker dense
                          label="Prazo de entrega"
                          input-class="required rounded-tag-small"
                          :error="$v.cotacao.prazoEntrega.$error"
                          @blur="$v.cotacao.prazoEntrega.$touch()"
                          v-model="cotacao.prazoEntrega"/>
        </v-col>
        <v-col class="d-flex" cols="12" sm="4">
          <px-date-picker dense
                          label="Prazo de resposta"
                          input-class="rounded-tag-small"
                          :error="$v.cotacao.validade.$error"
                          @blur="$v.cotacao.validade.$touch()"
                          v-model="cotacao.validade"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col class="d-flex" cols="12" sm="6">
          <px-select-enum dense
                          label="Forma pagamento"
                          class="rounded-tag-small"
                          v-model="cotacao.formaPagamento"
                          :items="formasPagamentos"
                          @change="onChangeFormaPagamento"/>
        </v-col>
        <v-col class="d-flex" cols="12" sm="6">
          <px-autocomplete-prazo-pagamento dense
                                           class="rounded-tag-small"
                                           v-model="cotacao.prazoPagamento"
                                           :disabled="isAVista"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="6">
          <px-autocomplete-projeto
            dense
            :disabled="isCentroCustoReadonly"
            :class="`rounded-tag-small ${isProjetoRequired ? 'required' : ''}`"
            :error="$v.cotacao.projeto.$error"
            @blur="$v.cotacao.projeto.$touch()"
            v-model="cotacao.projeto"/>
        </v-col>
        <v-col cols="12" sm="6">
          <px-autocomplete-centro-custo
            dense
            :disabled="isProjetoReadonly"
            :class="`rounded-tag-small ${isCentroCustoRequired ? 'required' : ''}`"
            :error="$v.cotacao.centroCusto.$error"
            @blur="$v.cotacao.centroCusto.$touch()"
            v-model="cotacao.centroCusto"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            dense
            outlined
            auto-grow
            hide-details
            rows="2"
            class="rounded-tag-small"
            label="Observações internas"
            v-model="cotacao.observacaoInterna"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea
            dense
            outlined
            auto-grow
            rows="2"
            counter="1500"
            class="rounded-tag-small"
            label="Observações externas"
            v-model="cotacao.observacao">
            <template v-slot:append-outer>
              <px-anexo :max="5"
                        v-model="cotacao.arquivos">
              </px-anexo>
            </template>
          </v-textarea>
        </v-col>
      </v-row>
       <v-row class="mt-3" dense>
        <v-col cols="12">
          <div class="font-weight-bold body-2">
            Finalidade da compra
          </div>
          <v-radio-group
            row
            dense
            v-model="cotacao.finalidadeCompra"
            class="d-flex">
            <v-radio v-for="finalidade in finalidades"
              :key="finalidade.key"
              :label="finalidade.description"
              :value="finalidade.key"/>
          </v-radio-group>
        </v-col>
      </v-row>
    </div>
    <div>
      <v-btn color="primary"
             class="rounded-tag-small normal-btn-text elevation-0"
            @click="enviar">
            Continuar
      </v-btn>
      <v-btn text
             class="normal-btn-text"
             @click="onClickCancel">
             Cancelar
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import {
  TipoFrete,
  FormaPagamento,
  FinalidadeCompra,
} from 'px-business-components';
import { required, or, requiredIf } from 'vuelidate/lib/validators';
import validationMixin from '@/mixins/validation-mixin';
import { isDateAfterOrSameToday } from '@/utils/date-utils';
import { isFieldReadonlyEntity, isFieldRequiredEntity } from '@/modules/erp-campos/erp-campos-utils';

const checkValidadeIsNull = validade => !validade;

export default {
  mixins: [validationMixin],
  props: {
    cotacao: {
      type: Object,
      required: true,
    },
    camposReadonly: {
      type: Object,
      default: null,
    },
    camposObrigatorios: {
      type: Object,
      default: null,
    },
    isFromRequisicao: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tiposFretes: TipoFrete.values,
      formasPagamentos: FormaPagamento.values,
      finalidades: FinalidadeCompra.values,
      finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
    };
  },
  computed: {
    isAVista() {
      return this.cotacao.formaPagamento !== FormaPagamento.keys.A_PRAZO.key;
    },
    isProjetoReadonly() {
      return isFieldReadonlyEntity('projeto', this.camposReadonly) && this.isFromRequisicao;
    },
    isCentroCustoReadonly() {
      return isFieldReadonlyEntity('centroCusto', this.camposReadonly) && this.isFromRequisicao;
    },
    isCentroCustoRequired() {
      return isFieldRequiredEntity('centroCusto', this.camposObrigatorios);
    },
    isProjetoRequired() {
      return isFieldRequiredEntity('projeto', this.camposObrigatorios);
    },
  },
  methods: {
    onChangeFormaPagamento() {
      this.cotacao.prazoPagamento = null;
    },
    enviar() {
      if (this.validateForm()) {
        return;
      }
      this.$emit('ready');
    },
    onClickCancel() {
      this.$emit('cancel');
    },
  },
  validations: {
    cotacao: {
      comprador: {
        required,
      },
      locaisEntrega: {
        required,
      },
      prazoEntrega: {
        required,
        isDateAfterOrSameToday,
      },
      centroCusto: {
        required: requiredIf(function () {
          return this.isCentroCustoRequired;
        }),
      },
      projeto: {
        required: requiredIf(function () {
          return this.isProjetoRequired;
        }),
      },
      validade: {
        // checa se é null ai ele deixa passar, se não for ele valida sendo maior que o dia atual
        validate: or(isDateAfterOrSameToday, checkValidadeIsNull),
      },
    },
  },
};
</script>
